import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowRight, KeyRound } from "lucide-react";
import { ApiError } from "@/lib/api";
import { unscopedKey } from "@/lib/queryClient";
import { Button } from "@/components/ui/Button";
import {
  firstFlowError,
  kratosErrorCopy,
  nodeMessages,
  oidcLinkLoginRequest,
  oidcProviders,
  submitOidcProvider,
  type KratosOidcLinkLoginRequest,
} from "@/lib/kratos";
import {
  exchangeKratosSession,
  linkKratosSocialSession,
} from "@/features/auth/kratos/exchange";
import { useKratosFlow } from "@/features/auth/kratos/useKratosFlow";
import {
  conditionalUiAvailable,
  flowSupportsPasskeyLogin,
  passkeyLogin,
  webauthnSupported,
} from "@/features/auth/kratos/webauthn";

type SocialAccountLinkRequest = {
  email: string;
  provider: string;
  providerLabel: string;
};

/**
 * Kratos-mode sign-in: email + password form, passkey button, and
 * conditional-UI passkey autofill — all rendered by the SPA, zero
 * redirects. A completed Kratos ceremony is mirrored into the Tktic
 * app session via `POST /api/auth/session/exchange`.
 */
export function KratosLoginPanel({
  nextPath,
  flowId,
  autoExchange = false,
  selfServiceEnabled = true,
  socialLoginEnabled = false,
  googleEnabled = true,
  appleEnabled = true,
}: {
  nextPath: string;
  flowId?: string;
  autoExchange?: boolean;
  selfServiceEnabled?: boolean;
  socialLoginEnabled?: boolean;
  googleEnabled?: boolean;
  appleEnabled?: boolean;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [exchangeError, setExchangeError] = useState<string | null>(null);
  const [socialError, setSocialError] = useState<string | null>(null);
  const [socialBusy, setSocialBusy] = useState<string | null>(null);
  const [passkeyBusy, setPasskeyBusy] = useState(false);
  const [linkRequest, setLinkRequest] = useState<SocialAccountLinkRequest | null>(null);
  const [linkPassword, setLinkPassword] = useState("");
  const [linking, setLinking] = useState(false);
  const [linkError, setLinkError] = useState<string | null>(null);
  const [relinkPassword, setRelinkPassword] = useState("");
  const [relinkError, setRelinkError] = useState<string | null>(null);
  const socialReturnTo = useMemo(
    () => socialAuthReturnTo("/login", nextPath),
    [nextPath],
  );

  const finishSignIn = async () => {
    setExchangeError(null);
    try {
      await exchangeKratosSession();
      await queryClient.invalidateQueries();
      await queryClient.invalidateQueries({ queryKey: unscopedKey("me") });
      void navigate({ to: nextPath, replace: true });
    } catch (err) {
      const pendingLink = accountLinkRequestFromError(err);
      if (pendingLink) {
        setLinkRequest(pendingLink);
        setLinkPassword("");
        setLinkError(null);
        setSocialError(null);
        return;
      }
      setExchangeError(exchangeErrorCopy(err));
    }
  };

  const { flow, loading, submitting, error, submit } = useKratosFlow("login", {
    flowId,
    createParams: { return_to: socialReturnTo },
    onSuccess: finishSignIn,
  });
  // Latest submit in a ref so the one-shot conditional-UI ceremony
  // below doesn't restart every time the flow object changes identity.
  const submitRef = useRef(submit);
  submitRef.current = submit;

  // Kratos OIDC re-link login flow: the verified social email already
  // belongs to a Kratos identity (e.g. the user unlinked Google then signed
  // in with Google again), so Kratos asks them to authenticate with their
  // existing password to re-attach the provider. The flow already carries
  // the identifier; submit it with the confirmed password through the same
  // login flow, then `finishSignIn` exchanges the resulting session.
  const relinkRequest = useMemo<KratosOidcLinkLoginRequest | null>(
    () => (flow ? oidcLinkLoginRequest(flow) : null),
    [flow],
  );

  const autoExchangeStarted = useRef(false);
  useEffect(() => {
    if (!autoExchange || autoExchangeStarted.current) return;
    autoExchangeStarted.current = true;
    void finishSignIn();
    // One-shot resume after a provider redirects back to /login.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoExchange]);

  // Conditional-UI autofill: arm one pending assertion per flow; when
  // the user picks a passkey from the email input's autofill UI, the
  // promise resolves and we auto-submit.
  const passkeyAvailable =
    !linkRequest && !relinkRequest && !!flow && flowSupportsPasskeyLogin(flow);
  const flowForAutofill = passkeyAvailable ? flow : null;
  useEffect(() => {
    if (!flowForAutofill) return;
    const controller = new AbortController();
    let cancelled = false;
    void (async () => {
      if (!(await conditionalUiAvailable())) return;
      try {
        const assertion = await passkeyLogin(flowForAutofill, {
          conditional: true,
          signal: controller.signal,
        });
        if (!cancelled) {
          await submitRef.current({ method: "passkey", passkey_login: assertion });
        }
      } catch {
        // Aborted (unmount/re-arm) or dismissed — silent by design.
      }
    })();
    return () => {
      cancelled = true;
      controller.abort();
    };
    // Re-arm only when the underlying flow (and its challenge) changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flowForAutofill?.id]);

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting || !flow) return;
    setExchangeError(null);
    setLinkRequest(null);
    setLinkPassword("");
    setLinkError(null);
    await submit({ method: "password", identifier: email.trim(), password });
  };

  const handleRelinkSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting || !flow || !relinkRequest) return;
    setRelinkError(null);
    try {
      await submit({
        method: "password",
        identifier: relinkRequest.email,
        password: relinkPassword,
      });
    } catch (err) {
      setRelinkError(kratosErrorCopy(err));
    }
  };

  // The re-link prompt is derived from the resumed Kratos flow, so it can't
  // be dismissed by clearing local state (unlike the social-link confirm).
  // Drop the `?flow=` deep link and land on a clean /login, which starts a
  // fresh login flow with the normal email/password + passkey options.
  const handleRelinkCancel = () => {
    setRelinkPassword("");
    setRelinkError(null);
    void navigate({ to: "/login", replace: true });
  };

  const handlePasskeyClick = async () => {
    if (!flow || passkeyBusy) return;
    setPasskeyBusy(true);
    setExchangeError(null);
    setLinkRequest(null);
    setLinkPassword("");
    setLinkError(null);
    try {
      const assertion = await passkeyLogin(flow);
      await submit({ method: "passkey", passkey_login: assertion });
    } catch {
      // User dismissed the platform dialog — nothing to surface.
    } finally {
      setPasskeyBusy(false);
    }
  };

  const handleSocialClick = async (providerId: string) => {
    if (!flow || socialBusy) return;
    setSocialBusy(providerId);
    setSocialError(null);
    setExchangeError(null);
    setLinkRequest(null);
    setLinkPassword("");
    setLinkError(null);
    try {
      const outcome = await submitOidcProvider("login", flow, providerId);
      if (outcome.kind === "success") {
        await finishSignIn();
      } else if (outcome.kind === "flow") {
        setSocialError(firstFlowError(outcome.flow) ?? "Social sign-in could not start.");
        setSocialBusy(null);
      }
    } catch (err) {
      setSocialError(kratosErrorCopy(err));
      setSocialBusy(null);
    }
  };

  const handleLinkSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!linkRequest || linking) return;
    setLinking(true);
    setLinkError(null);
    setExchangeError(null);
    try {
      await linkKratosSocialSession(linkPassword);
      await queryClient.invalidateQueries();
      await queryClient.invalidateQueries({ queryKey: unscopedKey("me") });
      void navigate({ to: nextPath, replace: true });
    } catch (err) {
      setLinkError(accountLinkErrorCopy(err));
    } finally {
      setLinking(false);
    }
  };

  const flowError = flow ? firstFlowError(flow) : null;
  const identifierMessages = flow ? nodeMessages(flow, "identifier") : [];
  const passwordMessages = flow ? nodeMessages(flow, "password") : [];
  const socialProviders = flow
    ? oidcProviders(flow).filter((provider) =>
        socialProviderAllowed(provider.id, {
          socialLoginEnabled,
          googleEnabled,
          appleEnabled,
        }) && !provider.disabled,
      )
    : [];

  if (loading) {
    return (
      <p data-testid="login-kratos-loading" className="font-mono text-xs text-ink-muted">
        Preparing sign-in...
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-4" data-testid="login-kratos-panel">
      {relinkRequest ? (
        <OidcRelinkConfirm
          request={relinkRequest}
          password={relinkPassword}
          error={relinkError ?? firstFlowError(flow!) ?? error}
          busy={submitting}
          onPasswordChange={setRelinkPassword}
          onSubmit={handleRelinkSubmit}
          onUseAnotherMethod={handleRelinkCancel}
        />
      ) : linkRequest ? (
        <SocialAccountLinkConfirm
          request={linkRequest}
          password={linkPassword}
          error={linkError}
          busy={linking}
          onPasswordChange={setLinkPassword}
          onSubmit={handleLinkSubmit}
          onCancel={() => {
            setLinkRequest(null);
            setLinkPassword("");
            setLinkError(null);
            setSocialBusy(null);
          }}
        />
      ) : (
        <form
          onSubmit={handlePasswordSubmit}
          data-testid="login-kratos-form"
          className="flex flex-col gap-4"
        >
        {socialProviders.length ? (
          <div
            data-testid="login-social-providers"
            className="grid gap-2 sm:grid-cols-2"
          >
            {socialProviders.map((provider) => (
              <Button
                key={provider.id}
                type="button"
                variant="outline"
                onClick={() => void handleSocialClick(provider.id)}
                disabled={!!socialBusy || submitting}
                data-testid={`login-social-${provider.id.toLowerCase()}`}
                className="w-full"
              >
                <SocialProviderMark providerId={provider.id} />
                {socialBusy === provider.id
                  ? "Opening..."
                  : `Continue with ${provider.label}`}
              </Button>
            ))}
          </div>
        ) : null}
        <label className="flex flex-col gap-1 text-xs">
          <span className="font-display uppercase tracking-wider text-ink-muted">
            Email
          </span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            // `webauthn` in autocomplete opts this input into the
            // passkey conditional-UI (autofill) flow armed above.
            autoComplete="username webauthn"
            autoFocus
            required
            data-testid="login-kratos-email"
            className="rounded border border-rule bg-canvas px-3 py-2.5 text-[16px] text-ink focus:outline-none focus:ring-2 focus:ring-accent sm:text-sm"
          />
          {identifierMessages.map((message) => (
            <span key={message} className="font-mono text-[0.65rem] text-destructive">
              {message}
            </span>
          ))}
        </label>
        <label className="flex flex-col gap-1 text-xs">
          <span className="font-display uppercase tracking-wider text-ink-muted">
            Password
          </span>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
            data-testid="login-kratos-password"
            className="rounded border border-rule bg-canvas px-3 py-2.5 text-[16px] text-ink focus:outline-none focus:ring-2 focus:ring-accent sm:text-sm"
          />
          {passwordMessages.map((message) => (
            <span key={message} className="font-mono text-[0.65rem] text-destructive">
              {message}
            </span>
          ))}
        </label>

        {flowError ? (
          <p data-testid="login-kratos-error" className="font-mono text-xs text-destructive">
            {flowError}
          </p>
        ) : null}
        {error ? (
          <p data-testid="login-kratos-client-error" className="font-mono text-xs text-destructive">
            {error}
          </p>
        ) : null}
        {socialError ? (
          <p data-testid="login-social-error" className="font-mono text-xs text-destructive">
            {socialError}
          </p>
        ) : null}
        {exchangeError ? (
          <div
            data-testid="login-kratos-exchange-error"
            className="flex flex-col gap-1 font-mono text-xs text-destructive"
          >
            <span>{exchangeError}</span>
            {exchangeError.includes("verify") ? (
              <Link
                to="/verify-email"
                data-testid="login-kratos-verify-link"
                className="self-start text-accent hover:underline"
              >
                Verify your email
              </Link>
            ) : null}
          </div>
        ) : null}

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end">
          {passkeyAvailable && webauthnSupported() ? (
            <Button
              type="button"
              variant="outline"
              onClick={() => void handlePasskeyClick()}
              disabled={passkeyBusy || submitting}
              data-testid="login-kratos-passkey"
              className="w-full sm:w-auto"
            >
              <KeyRound aria-hidden="true" />
              {passkeyBusy ? "Waiting for passkey..." : "Sign in with a passkey"}
            </Button>
          ) : null}
          <Button
            type="submit"
            disabled={submitting}
            data-testid="login-kratos-submit"
            className="w-full sm:w-auto"
          >
            {submitting ? (
              "Signing in..."
            ) : (
              <>
                Sign in
                <ArrowRight aria-hidden="true" />
              </>
            )}
          </Button>
        </div>
      </form>
      )}

      <footer className="flex flex-wrap items-center justify-between gap-2 border-t border-rule pt-3 text-[0.65rem] font-mono text-ink-muted">
        <Link
          to="/recovery"
          data-testid="login-kratos-recovery"
          className="rounded px-1 py-0.5 hover:text-ink hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        >
          Forgot password?
        </Link>
        {selfServiceEnabled ? (
          <Link
            to="/register"
            data-testid="login-register"
            className="rounded px-1 py-0.5 hover:text-ink hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          >
            Create account
          </Link>
        ) : null}
      </footer>
    </div>
  );
}

function SocialAccountLinkConfirm({
  request,
  password,
  error,
  busy,
  onPasswordChange,
  onSubmit,
  onCancel,
}: {
  request: SocialAccountLinkRequest;
  password: string;
  error: string | null;
  busy: boolean;
  onPasswordChange: (value: string) => void;
  onSubmit: (event: React.FormEvent) => void;
  onCancel: () => void;
}) {
  return (
    <form
      onSubmit={onSubmit}
      data-testid="login-social-link-panel"
      className="flex flex-col gap-4 rounded border border-rule bg-surface p-3"
    >
      <div className="flex flex-col gap-1">
        <p className="text-sm font-semibold text-ink">
          This email already has a Tktic account.
        </p>
        <p className="text-xs leading-5 text-ink-muted">
          Confirm your password to link {request.providerLabel} for {request.email}.
        </p>
      </div>

      <label className="flex flex-col gap-1 text-xs">
        <span className="font-display uppercase tracking-wider text-ink-muted">
          Account password
        </span>
        <input
          type="password"
          value={password}
          onChange={(e) => onPasswordChange(e.target.value)}
          autoComplete="current-password"
          autoFocus
          required
          data-testid="login-social-link-password"
          className="rounded border border-rule bg-canvas px-3 py-2.5 text-[16px] text-ink focus:outline-none focus:ring-2 focus:ring-accent sm:text-sm"
        />
      </label>

      {error ? (
        <p data-testid="login-social-link-error" className="font-mono text-xs text-destructive">
          {error}
        </p>
      ) : null}

      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={busy}
          data-testid="login-social-link-cancel"
          className="w-full sm:w-auto"
        >
          Use another method
        </Button>
        <Button
          type="submit"
          disabled={busy}
          data-testid="login-social-link-submit"
          className="w-full sm:w-auto"
        >
          {busy ? (
            "Linking..."
          ) : (
            <>
              Link {request.providerLabel}
              <ArrowRight aria-hidden="true" />
            </>
          )}
        </Button>
      </div>
    </form>
  );
}

function OidcRelinkConfirm({
  request,
  password,
  error,
  busy,
  onPasswordChange,
  onSubmit,
  onUseAnotherMethod,
}: {
  request: KratosOidcLinkLoginRequest;
  password: string;
  error: string | null;
  busy: boolean;
  onPasswordChange: (value: string) => void;
  onSubmit: (event: React.FormEvent) => void;
  onUseAnotherMethod: () => void;
}) {
  return (
    <form
      onSubmit={onSubmit}
      data-testid="login-oidc-relink-panel"
      className="flex flex-col gap-4 rounded border border-rule bg-surface p-3"
    >
      <div className="flex flex-col gap-1">
        <p className="text-sm font-semibold text-ink">
          Confirm your password to reconnect {request.providerLabel}.
        </p>
        <p className="text-xs leading-5 text-ink-muted">
          {request.email} already has a Tktic account. Sign in with your
          password to add {request.providerLabel} back as a sign-in method.
        </p>
      </div>

      <label className="flex flex-col gap-1 text-xs">
        <span className="font-display uppercase tracking-wider text-ink-muted">
          Email
        </span>
        <input
          type="email"
          value={request.email}
          readOnly
          data-testid="login-oidc-relink-email"
          className="rounded border border-rule bg-elevated px-3 py-2.5 text-[16px] text-ink-muted sm:text-sm"
        />
      </label>

      <label className="flex flex-col gap-1 text-xs">
        <span className="font-display uppercase tracking-wider text-ink-muted">
          Account password
        </span>
        <input
          type="password"
          value={password}
          onChange={(e) => onPasswordChange(e.target.value)}
          autoComplete="current-password"
          autoFocus
          required
          data-testid="login-oidc-relink-password"
          className="rounded border border-rule bg-canvas px-3 py-2.5 text-[16px] text-ink focus:outline-none focus:ring-2 focus:ring-accent sm:text-sm"
        />
      </label>

      {error ? (
        <p data-testid="login-oidc-relink-error" className="font-mono text-xs text-destructive">
          {error}
        </p>
      ) : null}

      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end">
        <Button
          type="button"
          variant="outline"
          onClick={onUseAnotherMethod}
          disabled={busy}
          data-testid="login-oidc-relink-cancel"
          className="w-full sm:w-auto"
        >
          Use password or passkey instead
        </Button>
        <Button
          type="submit"
          disabled={busy}
          data-testid="login-oidc-relink-submit"
          className="w-full sm:w-auto"
        >
          {busy ? (
            "Reconnecting..."
          ) : (
            <>
              Reconnect {request.providerLabel}
              <ArrowRight aria-hidden="true" />
            </>
          )}
        </Button>
      </div>
    </form>
  );
}

function socialAuthReturnTo(pathname: "/login" | "/register", nextPath?: string): string {
  const url = new URL(pathname, window.location.origin);
  url.searchParams.set("social", "1");
  if (nextPath && nextPath !== "/hub") {
    url.searchParams.set("next", nextPath);
  }
  return url.toString();
}

function socialProviderAllowed(
  providerId: string,
  options: {
    socialLoginEnabled: boolean;
    googleEnabled: boolean;
    appleEnabled: boolean;
  },
): boolean {
  if (!options.socialLoginEnabled) return false;
  const id = providerId.trim().toLowerCase();
  if (id === "google") return options.googleEnabled;
  if (id === "apple") return options.appleEnabled;
  return false;
}

function SocialProviderMark({ providerId }: { providerId: string }) {
  const id = providerId.trim().toLowerCase();
  const label = id === "google" ? "G" : id === "apple" ? "ID" : "SSO";
  return (
    <span
      aria-hidden="true"
      className="flex h-5 min-w-5 shrink-0 items-center justify-center rounded border border-rule bg-elevated px-1 font-display text-[0.62rem] font-semibold uppercase leading-none text-ink"
    >
      {label}
    </span>
  );
}

function accountLinkRequestFromError(err: unknown): SocialAccountLinkRequest | null {
  if (!(err instanceof ApiError)) return null;
  const detail = err.detail;
  if (!detail || typeof detail !== "object") return null;
  const code = "code" in detail ? String((detail as { code: unknown }).code) : "";
  if (code !== "account_link_required") return null;
  const email = "email" in detail ? String((detail as { email: unknown }).email) : "";
  const provider =
    "provider" in detail ? String((detail as { provider: unknown }).provider) : "";
  const providerLabel =
    "provider_label" in detail
      ? String((detail as { provider_label: unknown }).provider_label)
      : providerDisplayName(provider);
  return {
    email,
    provider,
    providerLabel: providerLabel || providerDisplayName(provider),
  };
}

function providerDisplayName(provider: string): string {
  const id = provider.trim().toLowerCase();
  if (id === "google") return "Google";
  if (id === "apple") return "Apple ID";
  return "this social account";
}

function accountLinkErrorCopy(err: unknown): string {
  if (err instanceof ApiError) {
    const code =
      err.detail && typeof err.detail === "object" && "code" in err.detail
        ? String((err.detail as { code: unknown }).code)
        : "";
    if (code === "invalid_password") {
      return "That password did not match this Tktic account.";
    }
    if (code === "disabled_user") {
      return "This Tktic account is disabled. Contact your team admin for access.";
    }
    if (code === "social_login_disabled" || code === "social_provider_disabled") {
      return "That social sign-in option is currently disabled.";
    }
    if (code === "account_link_conflict") {
      return "This social account is already linked to another Tktic account.";
    }
    if (err.status === 429) {
      return "Too many password attempts. Try again in a minute.";
    }
    return "Could not link this social account. Please try again.";
  }
  return "Network error. Try again.";
}

function exchangeErrorCopy(err: unknown): string {
  if (err instanceof ApiError) {
    const code =
      err.detail && typeof err.detail === "object" && "code" in err.detail
        ? String((err.detail as { code: unknown }).code)
        : "";
    if (code === "verified_email_required") {
      return "Please verify your email before signing in.";
    }
    if (code === "disabled_user") {
      return "This Tktic account is disabled. Contact your team admin for access.";
    }
    if (code === "social_login_disabled" || code === "social_provider_disabled") {
      return "That social sign-in option is currently disabled.";
    }
    if (code === "registration_disabled") {
      return "Account registration is currently closed. Contact your club admin for access.";
    }
    if (err.status === 429) {
      return "Too many sign-in attempts. Try again in a minute.";
    }
    return "Sign-in could not finish. Please try again.";
  }
  return "Network error. Try again.";
}
